import { useEffect, useRef, useState } from "react";
import { Skull } from "lucide-react";

/**
 * EnrageOverlay — watches the enemy's HP ratio and, at ≤ 25 %, flashes the
 * screen crimson, applies a short screen-shake to the entire viewport, and
 * pops "ENRAGED" typography. Fires ONCE per boss encounter (dedup by enemy_name).
 *
 * Non-interactive overlay — pointer-events: none. Duration ~1.4s so it doesn't
 * block the player's next action.
 */
export default function EnrageOverlay({ enemyName, enemyHp, enemyMaxHp, isBoss }) {
  const [triggered, setTriggered] = useState(null); // { key, name } | null
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!isBoss || !enemyName || !enemyMaxHp) return;
    const ratio = enemyHp / enemyMaxHp;
    // Trigger ONCE per unique boss name (dedup so refresh/re-poll doesn't re-fire)
    if (ratio > 0 && ratio <= 0.25 && !seenRef.current.has(enemyName)) {
      seenRef.current.add(enemyName);
      setTriggered({ key: Date.now(), name: enemyName });
      // Apply screen-shake class on <body> for the shake duration
      document.body.classList.add("dt-screen-shake");
      const t1 = setTimeout(() => document.body.classList.remove("dt-screen-shake"), 900);
      const t2 = setTimeout(() => setTriggered(null), 1600);
      return () => { clearTimeout(t1); clearTimeout(t2); document.body.classList.remove("dt-screen-shake"); };
    }
    // Reset dedup when the boss changes or HP goes back up (revive scenario)
    if (!enemyName || enemyHp === 0) {
      seenRef.current.clear();
    }
  }, [enemyName, enemyHp, enemyMaxHp, isBoss]);

  if (!triggered) {
    return (
      <style>{`
        @keyframes dt-screen-shake-kf {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-4px, 3px); }
          20% { transform: translate(5px, -3px); }
          30% { transform: translate(-6px, -2px); }
          40% { transform: translate(4px, 4px); }
          50% { transform: translate(-3px, 3px); }
          60% { transform: translate(5px, -4px); }
          70% { transform: translate(-4px, 2px); }
          80% { transform: translate(3px, -3px); }
          90% { transform: translate(-2px, 3px); }
        }
        body.dt-screen-shake { animation: dt-screen-shake-kf 0.9s cubic-bezier(0.36,0.07,0.19,0.97) both; }
      `}</style>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[66] pointer-events-none flex items-center justify-center"
      data-testid="enrage-overlay"
    >
      {/* Full-screen crimson vignette */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(circle at 50% 50%, transparent 35%, rgba(138,3,3,0.55) 100%)",
          animation: "enrage-fade 1.6s ease-out both",
        }}
      />

      {/* Center typography */}
      <div
        className="relative text-center"
        style={{ animation: "enrage-typo 1.6s cubic-bezier(0.2,0.9,0.3,1.05) both" }}
      >
        <div className="flex items-center justify-center gap-3 text-[11px] tracking-[0.5em]" style={{ color: "#8a0303" }}>
          <Skull size={16} /> WOUNDED BEYOND REASON <Skull size={16} />
        </div>
        <h1
          className="font-heading italic mt-3"
          style={{
            fontSize: "clamp(4.5rem, 13vw, 9rem)",
            lineHeight: 0.9,
            color: "#f0e6d2",
            textShadow: "0 0 40px rgba(138,3,3,0.9), 0 4px 20px rgba(0,0,0,0.95), 0 0 8px #ff2a2a",
            letterSpacing: "0.02em",
          }}
        >
          ENRAGED
        </h1>
        <div className="text-[11px] tracking-[0.4em] mt-3" style={{ color: "#e25822" }}>
          {triggered.name.toUpperCase()} · 25% HP
        </div>
      </div>

      <style>{`
        @keyframes enrage-fade {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes enrage-typo {
          0%   { opacity: 0; transform: scale(0.6) rotate(-2deg); }
          15%  { opacity: 1; transform: scale(1.15) rotate(0.5deg); }
          40%  { transform: scale(1) rotate(0deg); }
          85%  { opacity: 1; transform: scale(1) rotate(0deg); }
          100% { opacity: 0; transform: scale(1.10) rotate(0deg); }
        }
        @keyframes dt-screen-shake-kf {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-4px, 3px); }
          20% { transform: translate(5px, -3px); }
          30% { transform: translate(-6px, -2px); }
          40% { transform: translate(4px, 4px); }
          50% { transform: translate(-3px, 3px); }
          60% { transform: translate(5px, -4px); }
          70% { transform: translate(-4px, 2px); }
          80% { transform: translate(3px, -3px); }
          90% { transform: translate(-2px, 3px); }
        }
        body.dt-screen-shake { animation: dt-screen-shake-kf 0.9s cubic-bezier(0.36,0.07,0.19,0.97) both; }
      `}</style>
    </div>
  );
}
